<script lang="ts">
  import { Chart, registerables } from 'chart.js'
  import { onDestroy, onMount } from 'svelte'
  import { getDonutGroups, getHistogramBins } from '../utils/datasets'
  import { colorDangerTransparent, colorPrimary, colorPrimaryTransparent, getGraphOptions } from '../utils/graph'
  import type { AssetEvent, Dataset } from '../types'

  export let dataset: Dataset | undefined = undefined
  export let event: AssetEvent
  export let maxAcceptable: number | undefined = undefined
  export let minAcceptable: number | undefined = undefined
  export let partitionKey: string = ''
  export let type: 'line' | 'bar' | 'doughnut' = 'line'

  Chart.register(...registerables)

  let canvasEl: HTMLCanvasElement | undefined
  let chart
  let resizeRaf: number | undefined

  function createChart (): void {
    if (!canvasEl) return

    if (chart) chart.destroy()

    const datasets: Dataset[] = []
    const labels: string[] = []

    let start: number | string = 0
    let end: number | string = 100
    if (dataset) {
      const xValues = Object.values(dataset.data).map(v => v.x).sort((a, b) => a - b)
      if (xValues.length) {
        start = xValues[0]
        end = xValues[xValues.length - 1]
      }
    }

    if (type === 'line' && dataset) {
      datasets.push({
        ...dataset,
        borderColor: colorPrimary,
        fill: false,
        pointBackgroundColor: colorPrimary,
        pointHitRadius: 15,
        pointHoverRadius: 5,
        pointRadius: 1,
        tension: 0.25,
      })

      // Add max and min backgrounds
      const acceptableBase = {
        backgroundColor: colorDangerTransparent,
        pointStyle: false,
        showLine: false,
      }
      if (maxAcceptable) {
        datasets.push({
          ...acceptableBase,
          data: [{ x: start, y: maxAcceptable }, { x: end, y: maxAcceptable }],
          fill: 'end',
          label: 'Max acceptable',
        })
      }
      if (minAcceptable) {
        datasets.push({
          ...acceptableBase,
          data: [{ x: start, y: minAcceptable }, { x: end, y: minAcceptable }],
          fill: 'start',
          label: 'Min acceptable',
        })
      }
    } else if (type === 'bar' && dataset) {
      // Create histogram bins from the data
      datasets.push({
        ...dataset,
        data: getHistogramBins(dataset.data),
        backgroundColor: colorPrimaryTransparent,
        barPercentage: 1.0,
        categoryPercentage: 1.0,
      })
    } else if (type === 'doughnut' && partitionKey) {
      const data: number[] = []
      const doughnutGroups = getDonutGroups(event, partitionKey)
      const totalValue = Object.values(doughnutGroups).reduce((a: number, b: number) => a + b, 0)
      Object.entries(doughnutGroups)
        .forEach(([label, value]) => {
          labels.push(`${label} = ${value} → ${(value / totalValue * 100).toPrecision(3)}%`)
          data.push(value)
        })
      datasets.push({ data })
    }

    const data = { datasets }
    if (type === 'doughnut') {
      data.labels = labels
    }
    chart = new Chart(canvasEl, {
      data,
      options: getGraphOptions(start, end, type, minAcceptable, maxAcceptable),
      type,
    })
  }

  function resizeHandler (): void {
    if (resizeRaf) cancelAnimationFrame(resizeRaf)

    resizeRaf = requestAnimationFrame(() => {
      if ((dataset || event) && canvasEl) {
        if (type === 'doughnut') {
          // Make sure the legend doesn't get clipped on lower resolutions.
          chart.options.plugins.legend.position = window.innerWidth > 600
            ? 'right'
            : 'bottom'
        }
        chart.update('none')
      }
      resizeRaf = undefined
    })
  }

  onMount(() => {
    window.addEventListener('resize', resizeHandler)
  })

  onDestroy(() => {
    if (resizeHandler) window.removeEventListener('resize', resizeHandler)
    if (resizeRaf) cancelAnimationFrame(resizeRaf)
  })

  $: if ((dataset || event) && canvasEl && type) createChart()
</script>

<canvas bind:this={canvasEl} />
